<template>
    <Transition name="fade">
        <div v-if="!isDismissed" v-bind="$attrs">
            <BaseCard padding="md" radius="xl" elevated data-testid="person-card">
                <ChatConnectionDisambiguation
                    v-if="isWrongPersonView"
                    auto-focus
                    show-cancel
                    allow-create
                    :exclude-connection-id="connectionUuid"
                    :initial-search-term="isNewConnection ? name : ''"
                    @select="handleWrongPersonSelect"
                    @create="handleWrongPersonCreate"
                    @cancel="cancelWrongPerson" />
                <form v-else-if="isNewConnection" data-testid="person-manual-entry" @submit.prevent="addNewPerson">
                    <div class="relative">
                        <p class="body-small-bold text-center text-ink">{{ t("chat.personCard.manualEntry.title", { name }) }}</p>
                        <div class="absolute right-0 top-1/2 -translate-y-1/2">
                            <BaseDropdownMenu :sections="correctionMenuSections" :aria-label="t('chat.personCard.corrections.menuLabel')" @select="handleCorrectionSelect">
                                <template #trigger>
                                    <button
                                        ref="correctionMenuTrigger"
                                        type="button"
                                        class="correction-menu-trigger"
                                        data-testid="person-corrections-menu"
                                        :aria-label="t('chat.personCard.corrections.menuLabel')">
                                        <i class="bi bi-three-dots text-lg/none" aria-hidden="true"></i>
                                    </button>
                                </template>
                            </BaseDropdownMenu>
                        </div>
                    </div>
                    <div class="mt-4 flex flex-col gap-2.5">
                        <BaseInput
                            :id="nameInputId"
                            v-model="form.name"
                            :aria-label="t('chat.personCard.manualEntry.nameLabel')"
                            :placeholder="t('chat.personCard.manualEntry.namePlaceholder')" />
                        <BaseInput
                            :id="roleInputId"
                            v-model="jobTitleInput"
                            :aria-label="t('chat.personCard.manualEntry.jobTitleLabel')"
                            :placeholder="t('chat.personCard.manualEntry.jobTitlePlaceholder')" />
                    </div>
                    <BaseButton type="submit" theme="primary" class="mt-5 w-full" :disabled="!isFormValid">{{ t("chat.personCard.manualEntry.submit") }}</BaseButton>
                </form>
                <template v-else>
                    <p v-if="isInactive" class="status-label">
                        <i class="bi bi-slash-circle" aria-hidden="true"></i>
                        Inactive
                    </p>
                    <div class="flex items-center gap-3">
                        <CollaborationProfileAvatar :name="name" :available="!showLinkProfileCard" aria-hidden="true" translate="no" />
                        <div class="flex min-w-0 grow flex-col gap-1">
                            <p class="nadia-chat-semibold leading-tight wrap-break-word text-ink">{{ name }}</p>
                            <p class="body-small text-ink-muted">{{ jobTitle || "Role not known" }}</p>
                        </div>
                        <BaseDropdownMenu :sections="correctionMenuSections" :aria-label="t('chat.personCard.corrections.menuLabel')" @select="handleCorrectionSelect">
                            <template #trigger>
                                <button
                                    ref="correctionMenuTrigger"
                                    type="button"
                                    class="correction-menu-trigger"
                                    data-testid="person-corrections-menu"
                                    :aria-label="t('chat.personCard.corrections.menuLabel')">
                                    <i class="bi bi-three-dots text-lg/none" aria-hidden="true"></i>
                                </button>
                            </template>
                        </BaseDropdownMenu>
                    </div>
                    <HorizontalDivider />
                    <div v-if="isSearchOpen" class="flex flex-col gap-2" data-testid="link-profile-search">
                        <PublicProfileSearchCombobox
                            v-model="profileToLink"
                            auto-focus
                            :initial-search-term="name"
                            :disabled="isLinking"
                            :actions="inviteActions"
                            @action="handleCopyInvite"
                            @update:query="handleSearchQuery"
                            @search-error="searchError = $event">
                            <!-- The combobox owns selection; this row is presentation, not another button. It renders
                                 whether or not any profiles matched, so an empty search still offers the invite. -->
                            <template #action="{ hasNoMatch }">
                                <span class="flex w-full items-center gap-3 p-1" data-testid="invite-label">
                                    <span class="flex size-11 shrink-0 items-center justify-center rounded-full bg-surface-sunken text-ink-muted">
                                        <i class="bi text-2xl" :class="hasCopiedInvite ? 'bi-check-lg' : 'bi-link'" aria-hidden="true"></i>
                                    </span>
                                    <span class="body-small text-ink">{{ hasNoMatch ? t("chat.personCard.linkProfile.noMatch") : t("chat.personCard.linkProfile.notHere") }}</span>
                                    <span class="base-btn-neutral base-btn-sm ml-auto">{{
                                        hasCopiedInvite ? t("chat.personCard.linkProfile.copied") : t("chat.personCard.linkProfile.sendInvite")
                                    }}</span>
                                </span>
                            </template>
                        </PublicProfileSearchCombobox>
                        <BaseButton theme="primary" class="w-full" data-testid="link-profile-confirm" :disabled="!profileToLink || isLinking" @click="handleLink">
                            {{ t("chat.personCard.linkProfile.confirm") }}
                        </BaseButton>
                        <BaseButton theme="neutral" class="w-full" :disabled="isLinking" @click="handleCancelSearch">{{ t("chat.personCard.linkProfile.cancel") }}</BaseButton>
                        <BaseNotification v-if="searchError" theme="error" text="Searching for profiles failed." @dismiss="searchError = null" />
                        <BaseNotification v-if="linkProfileError" theme="error" text="Linking collaboration profile failed." @dismiss="linkProfileError = null" />
                        <BaseNotification v-if="inviteCopyFailed" theme="error" text="Copying the invite link failed." @dismiss="inviteCopyFailed = false" />
                    </div>
                    <template v-else>
                        <div class="mt-4">
                            <p v-if="summary" class="body-small mb-4 line-clamp-3 text-ink-muted" data-testid="person-summary">
                                {{ summary }}
                            </p>
                            <BaseButton :theme="showLinkProfileCard ? 'neutral' : 'primary'" class="flex w-full items-center justify-center gap-2" @click="handleViewPerson">
                                {{ t("chat.personCard.connected.showMore") }}
                                <i class="bi bi-box-arrow-up-right" aria-hidden="true"></i>
                            </BaseButton>
                        </div>
                        <BaseButton
                            v-if="showLinkProfileCard"
                            ref="linkProfileButton"
                            theme="primary"
                            class="mt-3 w-full"
                            data-testid="link-collaboration-profile"
                            @click="handleOpenSearch">
                            {{ t("chat.personCard.linkProfile.open") }}
                        </BaseButton>
                        <BaseButton v-if="canExploreCollaboration" theme="neutral" class="mt-3 w-full" :disabled="analysisBusy || analysisRequested" @click="exploreCollaboration">
                            {{ analysisRequested ? t("chat.personCard.exploration.requested") : t("chat.personCard.exploration.explore") }}
                        </BaseButton>
                    </template>
                </template>
            </BaseCard>
        </div>
    </Transition>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import type { GlobalProperties } from "~vue/appTypes";
import { ChatAction, type PluckedAction } from "~vue/chatActions";
import { copyShareUrl, profileInviteMessage } from "~vue/CollaborationProfile/composables/useProfileShareActions";
import { logProfileShared, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import type { ComboboxAction } from "~vue/components/BaseCombobox.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import CollaborationProfileAvatar from "~vue/components/CollaborationProfileAvatar.vue";
import BaseInput from "~vue/components/form/BaseInput.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import { useLinkPublicProfile } from "~vue/composables/useLinkPublicProfile";
import { useTimedShow } from "~vue/composables/useTimedShow.js";
import PublicProfileSearchCombobox from "~vue/Connections/PublicProfileSearchCombobox.vue";
import { i18n } from "~vue/i18n";
import type { ActionState, UpdateActionState } from "~vue/types/chat";
import type { Connection, ConnectionProp } from "~vue/types/connection";
import type { ExistingPublicProfile } from "~vue/types/profile";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { getFirstName } from "~vue/utils/stringUtils";
import { computed, type ComputedRef, inject, nextTick, onMounted, reactive, ref, useId, useTemplateRef, watch } from "vue";

import ChatConnectionDisambiguation from "./ChatConnectionDisambiguation.vue";
import { isUnconfirmedTaggedPerson } from "./taggedPerson";

const { t } = i18n.global;

type ChatPersonCardProps = {
    connectionChatMessageAction: PluckedAction<ChatAction.TAGGED_PERSON>;
    execute: (action: ChatAction, params: Record<string, unknown>, options?: Record<string, unknown>) => Promise<void>;
    csUuid?: string;
    connections?: ComputedRef<ConnectionProp[]>;
    publicProfileEnabled?: boolean;
    collaborationAnalysisEnabled?: boolean;
    collaborationAnalysisBusy?: ComputedRef<boolean>;
    collaborationExploredConnections?: ComputedRef<ReadonlySet<string>>;
    sendMessage?: (content: string, options?: { collaborationConnectionId: string }) => boolean;
    collaborationProfileInviteUrl?: string;
    showToast?: (message: string) => void;
    // The chat popover mounts the card when it opens, so mounting there means the user viewed it.
    logViewOnMount?: boolean;
};
const {
    connectionChatMessageAction,
    execute,
    csUuid = "",
    connections = computed(() => []),
    publicProfileEnabled = false,
    collaborationAnalysisEnabled = false,
    collaborationAnalysisBusy = computed(() => false),
    collaborationExploredConnections = computed(() => new Set<string>()),
    sendMessage,
    collaborationProfileInviteUrl = "",
    showToast,
    logViewOnMount = false,
} = defineProps<ChatPersonCardProps>();

// Popover roots inherit app context but not page-level provides; props carry that context.
const coachingSessionUuid = inject<string>("coachingSessionUuid", csUuid);
const allConnections = inject<ComputedRef<ConnectionProp[]>>("connections", connections);
const isPublicProfileEnabled = inject<boolean>("publicProfileEnabled", publicProfileEnabled);
const inviteUrl = inject<string>("collaborationProfileInviteUrl", collaborationProfileInviteUrl);
const showChatToast = inject<(message: string) => void>("showChatToast", showToast ?? (() => {}));

const updateActionState = inject<UpdateActionState>("updateActionState", () => {});
const { emitter } = inject<GlobalProperties>("globalProperties", {} as GlobalProperties);

const DEFAULT_ACTION_STATE: ActionState = { dismissed: false, minimized: false, submitted: false };

const revertActionState = (action: PluckedAction<ChatAction.TAGGED_PERSON>, patch: Partial<ActionState>) => {
    updateActionState({
        message_id: action.message.chat_message_id,
        lineIdx: action.lineIdx,
        action_state: { ...DEFAULT_ACTION_STATE, ...action.line.action_state, ...patch },
    });
};

const handleActionFailure = (error: unknown, action: string) => {
    logError(error, { context: { component: "ChatPersonCard", action }, tags: { error_type: "tagged_person_action" } });
    emitter.emit("error_notification", "Something went wrong updating this person. Please try again.");
};

const nameInputId = useId();
const roleInputId = useId();

const connectionUuid = computed(() => connectionChatMessageAction.line.action_params.connection);
const profileHref = computed(() => `/people/${connectionUuid.value}/`);
const currentConnection = computed(() => allConnections.value.find((conn) => conn.id === connectionUuid.value));
const name = computed(() => currentConnection.value?.name ?? connectionChatMessageAction.line.action_params.name);
const jobTitle = computed(() => currentConnection.value?.job_title ?? connectionChatMessageAction.line.action_params.title);
const isInactive = computed(() => currentConnection.value?.is_inactive ?? false);
const summary = computed(() => currentConnection.value?.summary);

const collaborationProfileShared = computed(
    () => currentConnection.value?.collaboration_profile_shared ?? connectionChatMessageAction.line.action_params.collaboration_profile_shared ?? false,
);

const isNewConnection = computed(() => !currentConnection.value?.public_profile && isUnconfirmedTaggedPerson(connectionChatMessageAction));
const isWrongPersonView = ref(false);
const correctionMenuTrigger = useTemplateRef<HTMLButtonElement>("correctionMenuTrigger");
const cancelWrongPerson = async () => {
    isWrongPersonView.value = false;
    await nextTick();
    // Return focus to the ⋯ menu the wrong-person view was opened from.
    correctionMenuTrigger.value?.focus();
};

const correctionMenuSections = computed<DropdownMenuSection[]>(() => [
    {
        id: "corrections",
        items: [
            { id: "wrong-person", type: "button", label: t("chat.personCard.corrections.wrongPerson"), icon: "bi bi-question-circle", testId: "corrections-wrong-person" },
            { id: "not-a-person", type: "button", label: t("chat.personCard.corrections.notAPerson"), icon: "bi bi-x-circle", testId: "corrections-not-a-person" },
        ],
    },
]);
const handleCorrectionSelect = (item: DropdownMenuItem) => {
    if (item.id === "wrong-person") {
        isWrongPersonView.value = true;
    } else if (item.id === "not-a-person") {
        void handleDecline();
    }
};

const correctPerson = async (subaction: "dismiss" | "reassign" | "reassign_new", params: Record<string, unknown> = {}) => {
    const action = connectionChatMessageAction;
    const connectionId = connectionUuid.value;
    try {
        await execute(
            ChatAction.TAGGED_PERSON,
            {
                message_id: action.message.chat_message_id,
                lineIdx: action.lineIdx,
                index: action.line.index,
                action_state: { dismissed: true },
                subaction,
                ...params,
            },
            { streaming: subaction !== "dismiss" },
        );
        logUserInteraction(
            subaction === "dismiss" ? AmplitudeEventNames.CONNECTION_DISMISSED : AmplitudeEventNames.CONNECTION_DISAMBIGUATED,
            {
                connection_id: params.correct_connection_id ?? connectionId,
                ...(subaction === "dismiss" ? { source: "decline_button" } : {}),
                ...(subaction === "reassign_new" ? { source: "wrong_person_add_new" } : {}),
            },
            coachingSessionUuid,
        );
    } catch (error) {
        revertActionState(action, { dismissed: false });
        handleActionFailure(error, subaction);
    } finally {
        router.reload({ only: ["connections"] });
    }
};
const handleWrongPersonSelect = (connection: Connection) => correctPerson("reassign", { correct_connection_id: connection.id });
const handleWrongPersonCreate = (personName: string) => correctPerson("reassign_new", { action_params: { name: personName } });
const handleDecline = () => correctPerson("dismiss");

const analysisEnabled = inject<boolean>("collaborationAnalysisEnabled", collaborationAnalysisEnabled);
const analysisBusy = inject<ComputedRef<boolean>>("collaborationAnalysisBusy", collaborationAnalysisBusy);
const exploredConnections = inject<ComputedRef<ReadonlySet<string>>>("collaborationExploredConnections", collaborationExploredConnections);
const sendChatMessage = inject<NonNullable<ChatPersonCardProps["sendMessage"]>>("sendMessage", sendMessage ?? (() => false));
const analysisRequested = ref(false);
watch(analysisBusy, (busy, wasBusy) => {
    if (wasBusy && !busy) {
        analysisRequested.value = false;
    }
});
const canExploreCollaboration = computed(
    () =>
        analysisEnabled &&
        !!currentConnection.value &&
        !currentConnection.value.declined_at &&
        currentConnection.value.collaboration_analysis_available === true &&
        !isInactive.value &&
        !isNewConnection.value &&
        !exploredConnections.value.has(connectionUuid.value),
);
const exploreCollaboration = () => {
    if (canExploreCollaboration.value && !analysisBusy.value && !analysisRequested.value) {
        analysisRequested.value = sendChatMessage(t("chat.personCard.exploration.message", { name: name.value }), { collaborationConnectionId: connectionUuid.value });
    }
};

const form = reactive({
    name: name.value,
    jobTitle: jobTitle.value,
});
const jobTitleInput = computed({
    get: () => form.jobTitle ?? "",
    set: (value: string) => {
        form.jobTitle = value;
    },
});

const isFormValid = computed(() => !!form.name?.trim());

const addNewPerson = async () => {
    if (!isFormValid.value) {
        return;
    }

    // Confirmation can switch this popover from the creation tag to the current mention while saving.
    const action = connectionChatMessageAction;
    try {
        await execute(ChatAction.TAGGED_PERSON, {
            message_id: action.message.chat_message_id,
            lineIdx: action.lineIdx,
            index: action.line.index,
            action_state: { submitted: true },
            action_params: {
                name: form.name,
                title: form.jobTitle,
            },
            subaction: "submit",
        });
        logUserInteraction(AmplitudeEventNames.CONNECTION_UPDATE, { source: "chat", connection_id: connectionUuid.value }, coachingSessionUuid);
    } catch (error) {
        revertActionState(action, { submitted: false });
        handleActionFailure(error, "submit");
    } finally {
        router.reload({ only: ["connections"] });
    }
};

const isProfileLinked = computed(() => !!currentConnection.value?.public_profile);
const isSearchOpen = ref(false);
const profileToLink = ref<ExistingPublicProfile | null>(null);
const searchError = ref<unknown>(null);

const inviteCopyFailed = ref(false);
const searchQuery = ref("");
const handleSearchQuery = (query: string) => {
    searchQuery.value = query;
    if (profileToLink.value && query !== (profileToLink.value.name ?? "")) {
        profileToLink.value = null;
    }
};

const searchedName = computed(() => searchQuery.value.trim() || name.value);
const inviteFirstName = computed(() => getFirstName(searchedName.value) || "them");

const showLinkSlot = computed(() => isPublicProfileEnabled && !isProfileLinked.value && !isNewConnection.value);
const showLinkProfileCard = computed(() => showLinkSlot.value && !collaborationProfileShared.value);

const { show: showCopied, isShowing: hasCopiedInvite } = useTimedShow();
const inviteActions = computed<ComboboxAction[]>(() => [{ id: "copy-invite", label: "Copy invite link", closeOnSelect: false }]);

const handleCopyInvite = async (id: ComboboxAction["id"]) => {
    if (id !== inviteActions.value[0].id) {
        return;
    }

    inviteCopyFailed.value = false;
    if (!(await copyShareUrl(withShareAttribution(inviteUrl, "chat", "link"), profileInviteMessage(inviteFirstName.value)))) {
        inviteCopyFailed.value = true;
        return;
    }
    showCopied();
    showChatToast(`Copied! Send the link to ${inviteFirstName.value} so they can complete their Collaboration Profile.`);
    logUserInteraction(AmplitudeEventNames.CONNECTION_INVITE_COPIED, { connection_id: connectionUuid.value, source: "chat" }, coachingSessionUuid);
    void logProfileShared({ shareSource: "chat", target: "link", shareKind: "invite", connectionId: connectionUuid.value }, coachingSessionUuid);
};

const linkProfileButton = useTemplateRef<{ $el: HTMLButtonElement }>("linkProfileButton");
const handleCancelSearch = async () => {
    isSearchOpen.value = false;
    profileToLink.value = null;
    logUserInteraction(
        AmplitudeEventNames.CONNECTION_LINK_SEARCH_CANCELED,
        { connection_id: connectionUuid.value, copied_invite: hasCopiedInvite.value, source: "chat" },
        coachingSessionUuid,
    );
    await nextTick();
    linkProfileButton.value?.$el.focus();
};

const {
    linkProfile,
    isLinking,
    error: linkProfileError,
} = useLinkPublicProfile({
    connectionId: connectionUuid,
    source: "chat",
    coachingSessionUuid,
    onLinked: () => {
        router.reload({
            only: ["connections"],
            onSuccess: () => {
                isSearchOpen.value = false;
                profileToLink.value = null;
            },
        });
    },
});

const handleOpenSearch = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_PROFILE_BUTTON_CLICKED, { connection_id: connectionUuid.value, source: "chat" }, coachingSessionUuid);
    isSearchOpen.value = true;
    inviteCopyFailed.value = false;
    profileToLink.value = null;
};

// Two-step link: picking a row from the combobox only stages the profile; the Link button commits it.
const handleLink = () => {
    if (profileToLink.value) {
        linkProfile(profileToLink.value.user);
    }
};

const handleViewPerson = () => {
    if (connectionUuid.value) {
        logUserInteraction(AmplitudeEventNames.CONNECTION_VIEW_PERSON_CLICKED, { connection_id: connectionUuid.value }, coachingSessionUuid);
        globalThis.open(profileHref.value, "_blank");
    }
};

onMounted(() => {
    if (logViewOnMount && connectionUuid.value && !isNewConnection.value) {
        logUserInteraction(AmplitudeEventNames.CONNECTION_CARD_VIEWED, { connection_id: connectionUuid.value }, coachingSessionUuid);
    }
});

const isDismissed = computed(() => connectionChatMessageAction.line.action_state?.dismissed);
</script>

<style scoped>
@reference "../../../css/tailwind.css";

.status-label {
    @apply scaffold-details-small mb-3 flex items-center gap-1 whitespace-nowrap text-ink-muted;
}

.correction-menu-trigger {
    @apply flex size-8 shrink-0 items-center justify-center rounded-lg text-ink-muted;
    @apply hover:bg-surface-sunken hover:text-ink data-[state=open]:bg-surface-sunken data-[state=open]:text-ink;
}
</style>
